<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { getReportTemplates, recordReportDownloadClick } from '../../api/reportApi.js'
import ReportPdfViewer from '../../report/components/ReportPdfViewer.vue'
import ReportTemplateSelect from '../../report/components/ReportTemplateSelect.vue'
import { findDefaultTemplate } from '../../report/reportTemplateUtils.js'
import AppAlert from '../../shared/components/AppAlert.vue'
import AppButton from '../../shared/components/AppButton.vue'

const props = defineProps({ reportId: { type: String, required: true } })
const pdfViewer = ref(null)
const error = ref('')
const isDownloading = ref(false)
const isPdfReady = ref(false)
const templates = ref([])
const selectedTemplateId = ref('')
const isTemplateLoading = ref(true)
const selectedTemplate = computed(() => templates.value.find(
  template => template.templateId === selectedTemplateId.value
))

async function loadTemplates() {
  isTemplateLoading.value = true
  try {
    const response = await getReportTemplates()
    templates.value = response.templates || []
    selectedTemplateId.value = findDefaultTemplate(templates.value)?.templateId || ''
    if (!selectedTemplateId.value) error.value = '사용 가능한 리포트 종류가 없습니다.'
  } catch (cause) {
    error.value = cause.message || '리포트 종류를 불러오지 못했습니다.'
  } finally {
    isTemplateLoading.value = false
  }
}

async function createPdfDownload() {
  if (isDownloading.value || !isPdfReady.value) return
  isDownloading.value = true
  error.value = ''
  try {
    pdfViewer.value?.download()
    recordReportDownloadClick(props.reportId, selectedTemplateId.value).catch(() => {})
  } catch (cause) {
    error.value = cause.message || 'PDF를 생성하지 못했습니다.'
  } finally {
    isDownloading.value = false
  }
}

function markPdfReady() {
  isPdfReady.value = true
  error.value = ''
}

function markPdfLoading() {
  isPdfReady.value = false
  error.value = ''
}

function reportPdfError(message) {
  isPdfReady.value = false
  error.value = message
}

watch(() => props.reportId, () => {
  isPdfReady.value = false
  error.value = ''
})

watch(selectedTemplateId, () => {
  isPdfReady.value = false
  error.value = ''
})

onMounted(loadTemplates)

</script>

<template>
  <section class="backend-report-panel">
    <div class="report-result-actions screen-only">
      <div>
        <p class="home-kicker">3단계 · 결과 리포트</p>
        <h1>건강 × 보장 분석 리포트</h1>
        <p class="page-lead">아래 리포트를 화면에서 보거나 PDF로 내려받을 수 있습니다.</p>
      </div>
      <div class="actions">
        <ReportTemplateSelect
          v-model="selectedTemplateId"
          :templates="templates"
          :loading="isTemplateLoading"
          :disabled="isTemplateLoading || isDownloading"
        />
        <AppButton variant="secondary" @click="$router.push('/history')">결과이력</AppButton>
        <AppButton size="lg" :disabled="isDownloading || !isPdfReady" @click="createPdfDownload">{{ isDownloading ? 'PDF 다운로드 중...' : 'PDF 다운로드' }}</AppButton>
      </div>
    </div>
    <AppAlert v-if="error" class="screen-only">{{ error }}</AppAlert>
    <ReportPdfViewer
      v-if="selectedTemplate"
      ref="pdfViewer"
      :report-id="reportId"
      :template-id="selectedTemplate.templateId"
      :template-version="selectedTemplate.templateVersion"
      :template-name="selectedTemplate.displayName"
      @loading="markPdfLoading"
      @ready="markPdfReady"
      @failed="reportPdfError"
    />
    <div v-else class="empty-box">{{ isTemplateLoading ? '리포트 종류를 불러오는 중입니다.' : '사용 가능한 리포트 종류가 없습니다.' }}</div>
  </section>
</template>
